import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import Head from 'next/head';

import { getProduct, getReviews, getProductLive, getSizeGuide, getIpadCountries, getSunneiProductData, getKboixLiveData } from 'actions/products';
import { getSeo, checkProfile } from 'actions/general';
import { getContactInfo } from 'actions/contact';
import { getStore } from 'actions/shops';
import { getProductsRecommended } from 'actions/products';

import { useI18n } from 'context/i18n';

import Layout from 'components/layout';

import { getMarket, getTargetIndex, getRealTarget, round, COOKIES, getRealID, targetsMap, handleStorage, getCookie } from 'utils/helpers';
import { AUTO_BONDS } from 'utils/constants/cookies';
import { initCMS } from 'utils/cms';
import { isClientSide } from 'utils';
import { ProductSheetContextProvider } from 'context/product-sheet';
import { getDomainValue } from 'utils/domain';
import { extractSku } from 'utils/product';

const ProductsModal = dynamic(() => import('components/productsModal'));
const ReserveModal = dynamic(() => import('components/reserveModal'));
const ProductBreadcrumbs = dynamic(() => import('components/productPurchaseCol/productBreadcrumbs').then((mod) => mod.ProductBreadcrumbs));

const ProductSheetDefault = dynamic(() => import('components/productSheet').then((mod) => mod.ProductSheetDefault));
const CamperlabProductSheet = dynamic(() => import('components/productSheet/camperlab-product-sheet').then((mod) => mod.CamperlabProductSheet));
const ProductSheetDIM = dynamic(() => import('components/productSheetDim'));
const ProductSheetRoku = dynamic(() => import('components/productSheetRoku'));

export const buildLdJson = (productSheet, url) => {
  const images = [];
  if (productSheet.images) {
    productSheet.images.forEach((value) => {
      // eslint-disable-next-line no-unused-expressions
      value.lg && images.push(value.lg);
    });
  }
  const result = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    '@id': url,
    name: productSheet.brand,
    sku: productSheet.code,
    image: images,
    description: productSheet.description || null,
    brand: {
      '@type': 'Brand',
      name: getDomainValue({
        camper: 'Camper',
        camperlab: 'Camperlab',
        nnormal: 'Nnormal',
      }),
    },
  };

  if (productSheet?.prices?.currencySap && productSheet?.prices?.current) {
    result.offers = {
      '@type': 'Offer',
      url,
      priceCurrency: productSheet?.prices?.currencySap ?? '-',
      price: productSheet?.prices?.current ? round(productSheet.prices.current, 0) : '-',
      itemCondition: 'https://schema.org/NewCondition',
      availability: 'https://schema.org/InStock',
      seller: {
        '@type': 'Organization',
        name: getDomainValue({
          camper: 'Camper',
          camperlab: 'Camperlab',
          nnormal: 'Nnormal',
        }),
      },
    };
  }

  if (productSheet?.reviewStatistics?.TotalReviewCount > 0) {
    result.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: Math.round(productSheet.reviewStatistics.AverageOverallRating * 10) / 10,
      reviewCount: productSheet.reviewStatistics.TotalReviewCount,
    };
  }

  if ('aggregateRating' in result || 'offers' in result) {
    // Product snippets require either review or aggregateRating or offers
    return result;
  }
  return {};
};

const pageMetaTags = (productSheet, market) =>
  getDomainValue({
    camper: [
      <meta key="twitter:card" name="twitter:card" content="product" />,
      <meta key="twitter:site" name="twitter:site" content="@Camper" />,
      <meta key="twitter:title" name="twitter:title" content={`${productSheet.brand} (${productSheet.code}) by Camper`} />,
      <meta key="twitter:description" name="twitter:description" content={productSheet.description} />,
      <meta key="twitter:data1" name="twitter:data1" content={productSheet.prices ? productSheet.prices.currentFormated : '-'} />,
      <meta key="twitter:label1" name="twitter:label1" content="Precio" />,
      <meta key="twitter:data2" name="twitter:data2" content={productSheet.brand} />,
      <meta key="twitter:label2" name="twitter:label2" content="Línea" />,
      <link key="canonical" rel="canonical" href={productSheet.baseUrl} />,
      <meta key="SKYPE_TOOLBAR" name="SKYPE_TOOLBAR" content="SKYPE_TOOLBAR_PARSER_COMPATIBLE" />,
      <meta key="og:type" property="og:type" content="product" />,
      <meta key="og:title" property="og:title" content={`${productSheet.isCamperOne ? 'CAMPER-ONE' : ''} ${productSheet.brand}  by Camper`} />,
      <meta key="og:description" property="og:description" content={productSheet.description} />,
      <meta
        key="og:image"
        property="og:image"
        content={productSheet.images && productSheet.images[0] && productSheet.images[0].lg ? productSheet.images[0].lg : ''}
      />,
      <meta key="og:site_name" property="og:site_name" content="Camper" />,
      <meta key="og:url" property="og:url" content={productSheet.baseUrl} />,
      <meta key="og:product:price:amount" name="og:product:price:amount" content={productSheet.prices ? round(productSheet.prices.current, 0) : '-'} />,
      <meta
        key="og:product:price:currency"
        property="og:product:price:currency"
        content={productSheet && productSheet.prices && productSheet.prices.currencySap ? productSheet.prices.currencySap : '-'}
      />,
      <meta key="og:price:amount" property="og:price:amount" content={productSheet.prices ? round(productSheet.prices.current, 0) : '-'} />,
      <meta key="og:availability" property="og:availability" content="instock" />,
      <script
        key="application/ld+json"
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(buildLdJson(productSheet, productSheet.baseUrl)) }}
      />,
    ],
    camperlab: [
      <meta key="twitter:card" name="twitter:card" content="product" />,
      <meta key="twitter:site" name="twitter:site" content="@Camper" />,
      <meta key="twitter:title" name="twitter:title" content={`${productSheet.brand} (${productSheet.code}) by Camper`} />,
      <meta key="twitter:description" name="twitter:description" content={productSheet.description} />,
      <meta key="twitter:data1" name="twitter:data1" content={productSheet.prices ? productSheet.prices.currentFormated : '-'} />,
      <meta key="twitter:label1" name="twitter:label1" content="Precio" />,
      <meta key="twitter:data2" name="twitter:data2" content={productSheet.brand} />,
      <meta key="twitter:label2" name="twitter:label2" content="Línea" />,
      <link key="canonical" rel="canonical" href={productSheet.baseUrl} />,
      <meta key="SKYPE_TOOLBAR" name="SKYPE_TOOLBAR" content="SKYPE_TOOLBAR_PARSER_COMPATIBLE" />,
      <meta key="og:type" property="og:type" content="product" />,
      <meta key="og:title" property="og:title" content={`${productSheet.isCamperOne ? 'CAMPER-ONE' : ''} ${productSheet.brand}  by Camper`} />,
      <meta key="og:description" property="og:description" content={productSheet.description} />,
      <meta
        key="og:image"
        property="og:image"
        content={productSheet.images && productSheet.images[0] && productSheet.images[0].lg ? productSheet.images[0].lg : ''}
      />,
      <meta key="og:site_name" property="og:site_name" content="Camper" />,
      <meta key="og:url" property="og:url" content={productSheet.baseUrl} />,
      <meta key="og:product:price:amount" name="og:product:price:amount" content={productSheet.prices ? round(productSheet.prices.current, 0) : '-'} />,
      <meta
        key="og:product:price:currency"
        property="og:product:price:currency"
        content={productSheet && productSheet.prices && productSheet.prices.currencySap ? productSheet.prices.currencySap : '-'}
      />,
      <meta key="og:price:amount" property="og:price:amount" content={productSheet.prices ? round(productSheet.prices.current, 0) : '-'} />,
      <meta key="og:availability" property="og:availability" content="instock" />,
      <script
        key="application/ld+json"
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(buildLdJson(productSheet, productSheet.baseUrl)) }}
      />,
    ],
    nnormal: [
      <link key="canonical" rel="canonical" href={productSheet.baseUrl} />,
      <meta key="SKYPE_TOOLBAR" name="SKYPE_TOOLBAR" content="SKYPE_TOOLBAR_PARSER_COMPATIBLE" />,
      <meta key="og:type" property="og:type" content="product" />,
      <meta key="og:title" property="og:title" content={`${productSheet.isCamperOne ? 'CAMPER-ONE' : ''} ${productSheet.brand}  by Nnormal`} />,
      <meta key="og:description" property="og:description" content={productSheet.description} />,
      <meta
        key="og:image"
        property="og:image"
        content={productSheet.images && productSheet.images[0] && productSheet.images[0].lg ? productSheet.images[0].lg : ''}
      />,
      <meta key="og:site_name" property="og:site_name" content={'Nnormal'} />,
      <meta key="og:url" property="og:url" content={productSheet.baseUrl} />,
      <meta key="og:product:price:amount" name="og:product:price:amount" content={productSheet.prices ? round(productSheet.prices.current, 0) : '-'} />,
      <meta
        key="og:product:price:currency"
        property="og:product:price:currency"
        content={productSheet && productSheet.prices && productSheet.prices.currencySap ? productSheet.prices.currencySap : '-'}
      />,
      <meta key="og:price:amount" property="og:price:amount" content={productSheet.prices ? round(productSheet.prices.current, 0) : '-'} />,
      <meta key="og:availability" property="og:availability" content="instock" />,
      <script
        key="application/ld+json"
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(buildLdJson(productSheet, productSheet.baseUrl)) }}
      />,
    ],
  });

// Because route can be [target]/[product]/[brand]/[code] or /producto?option=code we have to handle both cases. In the second case we don't have target, product and brand, so we have to get them from the productSheet.
export async function getServerSideProps(context) {
  const { req, resolvedUrl, query, locale, params } = context;
  try {
    const { lng, country } = query;
    const lang = ['default', 'int'].includes(locale) ? 'es_ES' : locale;
    const market = getMarket(locale);
    // cambio de profile para urls antiguas de app
    if (country && country !== market) {
      const newLang = lng || 'en';
      const [resolvedUrlBase, resolvedUrlParams] = resolvedUrl.split('?');
      const urlParamsArray = resolvedUrlParams.split('&').filter((item) => !(item.includes('lng=') || item.includes('country=')));
      const urlParamsString = urlParamsArray.join('&');

      return {
        redirect: {
          permanent: false,
          destination: `/${newLang}_${country.toUpperCase()}${resolvedUrlBase}?${urlParamsString}`,
        },
      };
    }

    const { store, usrhash, signature } = query;
    let email = null;

    try {
      if (store && usrhash && signature) {
        const data = await getContactInfo({
          context,
          profile: locale,
          email,
          usrhash,
          signature,
        });

        email = data.contact.email;
      }
    } catch (e) {
      console.error(e);
    }

    const extraData = {};
    // variable to know if the url is full ([target]/[product]/[brand]/[code]) or constrained (/producto?=option)
    const isFullRoute = query.option === undefined;
    // initialize variables
    const target = params.target === undefined ? '' : params.target;
    // const { code } = params;
    let targetIndex = isFullRoute ? getTargetIndex(target) : undefined;

    const realTarget = target === '' ? undefined : getRealTarget(target);
    // if it is not full route it will be assigned as undefined and later we assign it from the regex match
    let productCode = query.option;
    // let brand = isFullRoute ? params.brand : undefined;
    const isIpad = query.appIpad === 'true' || req.cookies[COOKIES.APP_IPAD] === 'true' || req.cookies[COOKIES.STORE_DATA] !== undefined;
    // if it is full route the getTargetIndex in line 79 will return null, so we throw here.
    if (targetIndex === null) {
      throw new Error('Target not found');
    }
    if (isFullRoute) {
      const skuInUrl = extractSku(params.code);
      if (!skuInUrl) {
        return {
          notFound: true,
        };
      }
      productCode = skuInUrl;
    }

    // Esto lleva aqui 2 años
    // Esto es algo temporal, asi que a futuro se borrara

    const SKU_REDIRECTS = {
      '20889-999-C001': '20889-999-C041',
      '20889-999-C002': '20889-999-C042',
      '20889-999-C003': '20889-999-C043',
      '20889-999-C004': '20889-999-C043',
      '20889-999-C007': '20889-999-C044',
      '20889-999-C008': '20889-999-C045',
      '20889-999-C009': '20889-999-C046',
      '20889-999-C010': '20889-999-C047',
      '20889-999-C011': '20889-999-C048',
      '20889-999-C012': '20889-999-C049',
      '20889-999-C013': '20889-999-C050',
      '20889-999-C014': '20889-999-C051',
      '20889-999-C015': '20889-999-C052',
      '20889-999-C016': '20889-999-C053',
      '18811-999-C001': '18811-999-C041',
      '18811-999-C002': '18811-999-C042',
      '18811-999-C003': '18811-999-C043',
      '18811-999-C006': '18811-999-C043',
      '18811-999-C007': '18811-999-C044',
      '18811-999-C008': '18811-999-C045',
      '18811-999-C009': '18811-999-C046',
      '18811-999-C010': '18811-999-C047',
      '18811-999-C011': '18811-999-C048',
      '18811-999-C012': '18811-999-C049',
      '18811-999-C013': '18811-999-C050',
      '18811-999-C014': '18811-999-C051',
      '18811-999-C016': '18811-999-C052',
      '17665-999-C001': '17665-999-C041',
      '17665-999-C002': '17665-999-C042',
      '17665-999-C003': '17665-999-C043',
      '17665-999-C004': '17665-999-C044',
      '17665-999-C005': '17665-999-C045',
      '17665-999-C006': '17665-999-C045',
      '20848-999-C007': '20848-999-C041',
      '20848-999-C008': '20848-999-C042',
      '20848-999-C009': '20848-999-C043',
      '20848-999-C010': '20848-999-C044',
      '20848-999-C011': '20848-999-C045',
      '20848-999-C012': '20848-999-C046',
      '16002-999-C022': '16002-999-C040',
      '16002-999-C030': '16002-999-C040',
      '16002-999-C031': '16002-999-C040',
      '16002-999-C033': '16002-999-C040',
      '16002-999-C034': '16002-999-C040',
      '27205-999-C023': '27205-999-C040',
      '27205-999-C025': '27205-999-C040',
      '27205-999-C026': '27205-999-C040',
      '27205-999-C027': '27205-999-C040',
      '27205-999-C029': '27205-999-C040',
      '27205-999-C030': '27205-999-C040',
      'N2ZKXM1-001': 'N2ZKXM1-999-K001',
    };

    if (SKU_REDIRECTS?.[productCode]) {
      const redirectUrl = `/${locale}${resolvedUrl.replace(productCode, SKU_REDIRECTS[productCode])}`;
      console.info('redirect new sku: ', redirectUrl);
      return {
        redirect: {
          permanent: true,
          destination: redirectUrl,
        },
      };
    }

    const SKU_REDIRECTS_BY_MARKET ={
      'US':{
        'N2AKX01-001': 'N2ZKXM1-999-K001', //In the US we cannot allow the insoles to be sold independently thanks to Mr. Trump
        'N2AKX02-001': 'N2ZKXM1-999-K002', //In the US we cannot allow the insoles to be sold independently thanks to Mr. Trump
        'N2AKX03-001': 'N2ZKXM1-999-K003', //In the US we cannot allow the insoles to be sold independently thanks to Mr. Trump
      }
    }
    if(SKU_REDIRECTS_BY_MARKET?.[market] && SKU_REDIRECTS_BY_MARKET[market]?.[productCode]){
      const redirectUrl = `/${locale}${resolvedUrl.replace(productCode, SKU_REDIRECTS_BY_MARKET[market][productCode])}`;
      console.info('redirect new sku: ', redirectUrl);
      return {
        redirect: {
          permanent: true,
          destination: redirectUrl,
        },
      };
    }

    const [response, profileData] = await Promise.all([
      getProduct({
        context,
        lang,
        target,
        productCode,
        targetIdUrl: realTarget,
      }),
      checkProfile({ context, profile: locale }),
    ]);
    // check if response is ok
    if (response?.productSheet === undefined || !response?.productSheet.exists) {
      // throw new Error('Product not found');
      return {
        notFound: true,
      };
    }

    // Si nos encontramos los productos de sunnei tenemos que hacer varias cosas, traernos todos los datos de sunnei + los liveResponse de cada uno de los productos de sunnei

    const productResponse = response.productSheet;
    const betterProduct = response.productBetter || null;
    const camperlab = productResponse.camperlab || false;
    const newShooting = productResponse.newShooting || false;
    const targetId = productResponse.target;
    const { seoContent } = response;

    let reviews = false;
    let reviews_lang = [];

    // En camperlab bloqueamos las reviews porque no salimos con ellas. No las quieren en las pdps y asi nos ahorramos una llamada
    if (getDomainValue({ camper: true, nnormal: true, camperlab: false }) && profileData?.config) {
      const { config } = profileData;

      if (config?.reviews && config?.reviews_lang) {
        ({ reviews, reviews_lang } = config);
      }
    }

    /**
     * Para obtener las tallas usamos realTarget (el target de la pagina actual) y no targetId. Se ha de hacer así, para los productos Unisex o Accesorios.
     * Because we don't have the realTarget in the /producto route we use the targetId in that case. Maybe we can map the targetId to the realTarget in the future.
     * If the realTarget is X in camperlab products for women, the sizes returned are for men, so we need to use the target in the url
     */
    const hasSunneiConfigInResponse = productResponse.sunneiInfo && Object.keys(productResponse.sunneiInfo).length > 0;

    /**
     * Para que un producto sea kboix tiene que tener el campo de kboix a true y tener suelas
     */
    const midsoles = Array.isArray(productResponse.kboixInfo?.plantillas) ? productResponse.kboixInfo.plantillas : [];

    const kboixPromise =
      !midsoles.length > 0 && !productResponse.kboixInfo?.casco ?
        null
      : getKboixLiveData({
          context,
          locale: lang,
          midsoles,
          casco: productResponse.kboixInfo?.casco,
          target,
        });
    const productRealTarget = getDomainValue({
      camper: targetId === 'X' && Object.keys(targetsMap).includes(realTarget) ? realTarget : targetId,
      camperlab: targetId,
      nnormal: targetId === 'X' && Object.keys(targetsMap).includes(realTarget) ? realTarget : targetId,
    });
    const [{ defaultProps, pdpProps }, productLiveResponse, sizeGuideResponse, countriesForIpad, reviewsResponse, sunneiData, kboixData] = await Promise.all([
      initCMS({
        context,
        locale: lang,
        tnmSelectedTarget: realTarget,
        pdpParams: [productCode, realTarget],
        camperlab,
      }),
      getProductLive({
        context,
        lang,
        target: productRealTarget,
        productCode,
      }),
      getSizeGuide({
        context,
        lang,
        material: productCode,
        // context, lang, target: isFullRoute ? target : 'X', product: isFullRoute ? product : productCode,
      }),
      isIpad ? getIpadCountries({ context, profile: lang }) : null,
      reviews ?
        await getReviews({
          context,
          lang,
          productCode,
          offset: 0,
          sortField: 'rating',
          sortType: 'desc',
          langList: reviews_lang,
        })
      : null,
      hasSunneiConfigInResponse ?
        await getSunneiProductData({
          context,
          locale: lang,
          sunneiInfo: productResponse.sunneiInfo,
          target: targetId,
        })
      : null,
      kboixPromise,
    ]);

    if (isIpad) {
      extraData.shippingCountries = countriesForIpad;
    }

    // get targetIndex from
    if (!isFullRoute) {
      const targetIndexes = ['M', 'W', 'K'];
      targetIndex = targetIndexes.indexOf(targetId) !== -1 ? targetIndexes.indexOf(targetId) : 0;
    }

    const targetSizeGuide =
      sizeGuideResponse && sizeGuideResponse.length > 0 && sizeGuideResponse[targetIndex] !== null && sizeGuideResponse[targetIndex] !== undefined ?
        sizeGuideResponse[targetIndex].guideSizes
      : null;
    const keyPosition = 0;
    const valuePosition = 1;

    // to-do [1]
    const guarantyInfoArray = Object.entries(productResponse)
      .filter((keyValueArray) => keyValueArray[keyPosition].includes('infoSheet'))
      .map((infoSheetKeyValueArray) => infoSheetKeyValueArray[valuePosition]);
    const careTextArray = Object.entries(productResponse)
      .filter((keyValueArray) => keyValueArray[keyPosition].includes('careText'))
      .map((infoSheetKeyValueArray) => infoSheetKeyValueArray[valuePosition]);
    const careDetailsArray = Object.entries(productResponse)
      .filter((keyValueArray) => keyValueArray[keyPosition].includes('careDetails'))
      .map((infoSheetKeyValueArray) => infoSheetKeyValueArray[valuePosition]);

    const averageRate =
      reviewsResponse?.BatchedResults?.q0?.Results.length > 0 && reviewsResponse?.BatchedResults?.q0?.Results[0].FilteredReviewStatistics?.AverageOverallRating;
    const reviewStatistics = reviewsResponse?.BatchedResults?.q0?.Results.length > 0 && reviewsResponse?.BatchedResults?.q0.Results[0].FilteredReviewStatistics;
    const firstReviews = reviewsResponse?.BatchedResults?.q1;
    const emptyReviews =
      (!averageRate && !reviewStatistics && firstReviews === undefined) ||
      (typeof firstReviews === 'object' && 'TotalResults' in firstReviews && firstReviews.TotalResults === 0);

    const isCamperOne = productResponse.camperone !== undefined && productResponse.camperone.jsonConfig !== undefined;
    const isRoku = isCamperOne && productResponse.isRoku;
    // El sizeType no viene correcto para las inspiraciones de sunnei asi que seteamos este valor a 'TALLAC' para que se muestren los sizes de la inspiracion
    const sizeType = hasSunneiConfigInResponse ? 'TALLAC' : productResponse.sizeType || null;
    const productCodeFeatures = getDomainValue({
      camper: null,
      camperlab: null,
      nnormal: productResponse?.codeFeatures ?? null,
    });
    const productSheet = {
      code: productResponse.id,
      exists: productResponse.exists || false,
      tipology: productResponse.tipology,
      category: productResponse.category || null,
      line: productResponse.line || null,
      concept: productResponse.concept || null,
      color: productResponse.color || '',
      codeDescription: productResponse?.subtitleH1 || null,
      codeFeatures: productCodeFeatures,
      brand: productResponse.name || null,
      nameBase: productResponse.nameBase || null,
      brandProducts: productResponse.productLines || null,
      sizes: sunneiData?.sizes ?? kboixData?.casco?.sizes ?? productLiveResponse?.productSheetLive?.sizes ?? [],
      description: productResponse.description || null,
      features: productResponse.features || null,
      productDescription: productResponse.productDescription || null,
      guarantyInfoArray,
      care: productResponse.care || null,
      careDetailsArray,
      careTextArray,
      prices: sunneiData?.price ?? productResponse.prices ?? productLiveResponse?.productSheetLive?.prices ?? null,
      rate: averageRate || null,
      reviewStatistics: reviewStatistics || null,
      firstReviews: firstReviews || null,
      emptyReviews,
      images: productResponse.views ?? null,
      imagesMobile: productResponse.viewsMobile ?? null,
      imagesRecently: productResponse.imagesRecently || null,
      imageBag: productResponse.imageBag || null,
      imageBagMini: productResponse.imageBagMini || null,
      sizeType,
      isAccessory: sizeType === null,
      sizesGuide: sizeGuideResponse || null,
      targetSizeGuide,
      camperone: productResponse.camperone || null,
      target: targetId || 'X', // different
      realTarget: productRealTarget,
      family: productResponse.family || null,
      betterProduct,
      camperlab, // different
      notifyMe: productResponse.notifyMe || false,
      notifyMeType: productResponse.notifyMeType || null,
      notifyMeSpecialLabel: productResponse.notifyMeSpecialLabel || false,
      isRecrafted: productResponse.recrafted || false,
      isGoretex3d: productResponse.goretex3d || false,
      isObraDeArte: productResponse.obraDeArte || false,
      isObraDeArteSonFortesa: productResponse.obraDeArteSonFortesa || false,
      baseUrl: productResponse.url,
      newShooting, // different
      textDiscountCollection: productResponse?.textDiscountCollection || '',
      textDiscountCollectionOld: productResponse?.textDiscountCollectionOld || '',
      giftCardSale: productResponse?.giftCardSale ?? false,
      giftCardAmounts: productResponse?.giftCardAmounts || false,
      giftCardMaxAmount: productResponse?.giftCardMaxAmount || false,
      giftCardMinAmount: productResponse?.giftCardMinAmount || 1,
      extraData,
      presales: productResponse?.presales || false,
      presalesDate: productResponse?.presalesDate || null,
      membersOnly: productResponse.membersOnly || false,
      productSheetInfo: productResponse?.productSheetInfo || null,
      sapActive: productResponse?.sapActive || false,
      colorLocale: productResponse.colorLocale || '',
      recycled: productResponse.recycled || false,
      isRoku,
      kboix: productResponse.kboix || false,
      kboixData: {
        midsoles: kboixData?.midsoles ? kboixData.midsoles : null,
        sku: productResponse?.kboixInfo?.casco ?? null,
        minExtra: productResponse?.kboixInfo?.minExtra ?? 2,
        canConfig: productResponse?.kboixInfo?.canConfig ?? true,
      },
      recycledStatus: productResponse.recycledStatus ?? null,
      voucherPrices: productLiveResponse?.productSheetLive?.voucherPrices || null,
      viewer3d: productResponse.viewer3d ?? false,
      isSunnei: productResponse.isSunnei || false,
      relevantInfo: productResponse.relevantInfo || '',
      sunneiInfo:
        !productResponse.sunneiInfo ? null : (
          {
            ...productResponse.sunneiInfo,
            products: sunneiData?.data ?? {},
            all: sunneiData?.skus ?? [],
          }
        ),
      breadcrumbs: productResponse.breadcrumbs ?? [],
      targetLinks: productResponse.targetLinks ?? [],
      productPartsSizing: productResponse.productPartsSizing ?? null,
      productMeasures: Array.isArray(productResponse.productMeasures) ? productResponse.productMeasures : null,
      productProcessing: productResponse.productProcessing || null,
      productProcessingText: productResponse.productProcessingText || null,
    };
    const seoFilters = [
      `profile=${lang}`,
      `family=${productSheet.family || '1'}`,
      `gender=${productSheet.target || 'X'}`,
      `option=${productSheet.target || 'X'}`,
      `concept=${encodeURI(productSheet.concept) || 'X'}`,
      'page=PRODUCTO',
      `material=${productSheet.code}`,
    ];
    if (productResponse.tipologyCode && productResponse.tipologyCode !== '') {
      seoFilters.push(`typology=${productResponse.tipologyCode}`);
    }
    const metatags = await getSeo(context, seoFilters);

    const breadcrumbI18n = {
      target: productSheet.target ? productSheet.target.toLocaleLowerCase() : null,
      family: productSheet.family || null,
    };

    const isReserveLink = store && email;
    let storeInfo = null;

    if (isReserveLink) {
      storeInfo = await getStore({ context, profile: locale, id: store });
    }
    const isRokuProduct = getDomainValue({
      camper: isRoku || false,
      camperlab: false,
      nnormal: false,
    });
    return {
      props: {
        defaultProps,
        productSheet,
        locale: lang,
        market,
        isCamperOne,
        isRoku: isRokuProduct,
        metatags,
        breadcrumbI18n,
        pdpProps: pdpProps || null,
        seoContent: seoContent || null,
        reserve: isReserveLink ? { store: storeInfo, email } : null,
      },
      // revalidate: 120,
    };
  } catch (error) {
    console.error(`Error: ${error}`);
    context.res.statusCode = 500;
    return {
      props: {
        error: `SSR Error: ${error}`,
        permanent: false,
      },
    };
  }
}

export default function ProductSheet({
  defaultProps,
  productSheet,
  locale,
  datetime,
  uuid,
  market,
  isCamperOne,
  isRoku = false,
  metatags,
  breadcrumbI18n,
  autoappliedVoucher = false,
  pdpProps = null,
  seoContent,
  reserve,
}) {
  const { t } = useI18n();
  const props = {
    productSheet,
    locale,
    datetime,
    uuid,
    market,
    metatags,
    pdpProps,
    seoContent,
  };

  const { prices } = productSheet;

  const showProductsModalDefault = prices === null || productSheet?.notifyMeType === 'OUT';

  const [shouldShowProductModal, setShouldShowProductModal] = useState(showProductsModalDefault);
  const [shouldShowReserveModal, setShouldShowReserveModal] = useState(reserve !== null);
  const [recommendedProducts, setRecommendedProducts] = useState(null);

  const router = useRouter();

  let { target, product } = router.query;
  const { option } = router.query;

  const targetRealID = getRealID('target', target);
  const familyRealID = getRealID('product', product);

  // If route is rewrite from /producto, target and product are incorrect/ get values from productSheet
  if (isClientSide() && option !== undefined) {
    const productPathName = new URL(productSheet.baseUrl).pathname;
    [, target, product] = productPathName.split('/').filter(Boolean);
  }

  useEffect(() => {
    // cambios de feat/7019 que no se choque con los cambios de feat/retain-scroll
    handleStorage('setItem', 'gridTarget', target);
    handleStorage('setItem', 'gridProduct', product);

    if (isClientSide()) {
      window.getProductsByAlgorithm = async (algorithm) => {
        try {
          const recommendedPayload = {
            lang: locale,
            productCode: productSheet.code,
            target: getDomainValue({
              camper: false,
              nnormal: productSheet.target,
              camperlab: false,
            }),
            targetIdUrl: targetRealID,
            algorithm,
            ia: 'true',
          };

          const autoVoucher = getCookie(COOKIES.AUTO_BONDS) || '';
          const voucher = getCookie(COOKIES.BONDS) || '';
          const voucherDiscount = autoVoucher && autoVoucher === AUTO_BONDS.APPLY;

          if (voucherDiscount) {
            recommendedPayload.voucher = voucher;
          }

          const recommendedResponse = await getProductsRecommended(recommendedPayload);
          setRecommendedProducts(recommendedResponse);
        } catch (e) {
          console.error('[Product Recommendation Error]', e);
        }
      };
    }
  }, []);

  let SheetContent = getDomainValue({
    camper: ProductSheetDefault,
    nnormal: ProductSheetDefault,
    camperlab: CamperlabProductSheet,
  });

  if (isCamperOne) {
    SheetContent = ProductSheetDIM;
  }
  if (isRoku) {
    SheetContent = ProductSheetRoku;
  }

  const gtmProps = {
    target,
    product,
    productSheet,
    targetRealID,
    familyRealID,
    url: productSheet?.baseUrl,
  };
  const productsModal = getDomainValue({
    camper:
      showProductsModalDefault ?
        <ProductsModal
          productSheet={{
            ...productSheet,
            id: productSheet.code,
            targetId: productSheet.target,
          }}
          isModalVisible={shouldShowProductModal}
          handleCancel={() => setShouldShowProductModal(false)}
          origin="sheet"
          realTarget={targetRealID}
          title={t('ficha.producto', 'sin.stock')}
          direct
        />
      : null,
    camperlab: null,
    nnormal: null,
  });
  const layoutBreadcrumbs = getDomainValue({
    camper: false,
    camperlab: false,
    nnormal: true,
  });
  const productBreadcrumbsFooter = getDomainValue({
    camper: <ProductBreadcrumbs withLd={true} variant="old" className="mt-12 px-4 py-2.5 camper:max-md:hidden" />,
    camperlab: null,
    nnormal: null,
  });
  return (
    <ProductSheetContextProvider data={productSheet}>
      <Layout
        {...defaultProps}
        breadcrumbs={layoutBreadcrumbs}
        breadcrumbI18n={breadcrumbI18n}
        breadcrumbsBottom
        camperlab={productSheet.camperlab}
        recycled={productSheet.recycled}
        scrollArrow="desktop"
        metatags={metatags}
        pageName={'detail'}
        gtmProps={gtmProps}
      >
        <Head>{pageMetaTags(productSheet, market)}</Head>
        <SheetContent {...props} recommendedProducts={recommendedProducts} autoappliedVoucher={autoappliedVoucher} />
        {productsModal}
        {reserve && (
          <ReserveModal productId={productSheet.code} reserve={reserve} visible={shouldShowReserveModal} onClose={() => setShouldShowReserveModal(false)} />
        )}
        {productBreadcrumbsFooter}
      </Layout>
    </ProductSheetContextProvider>
  );
}

ProductSheet.propTypes = {
  productSheet: PropTypes.object,
  datetime: PropTypes.string,
  uuid: PropTypes.string,
  locale: PropTypes.string,
  market: PropTypes.string,
  footerProps: PropTypes.any,
  isCamperOne: PropTypes.bool,
  isRoku: PropTypes.bool,
  kboix: PropTypes.bool,
  tnmProps: PropTypes.any,
  ribbon: PropTypes.any,
  metatags: PropTypes.object,
  breadcrumbI18n: PropTypes.object,
  autoappliedVoucher: PropTypes.bool,
  pdpProps: PropTypes.object,
  seoContent: PropTypes.object,
  reserve: PropTypes.object,
};

ProductSheet.i18nPage = 'ProductSheet';
